import {Injectable, Injector} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import Swal, {SweetAlertOptions, SweetAlertResult} from "sweetalert2";

@Injectable()
export class AlertService {

    constructor(
        private injector: Injector
    ) {}

    /**
     * TranslateService is resolved lazily: AlertService is used by HttpErrorInterceptor,
     * and TranslateService pulls in HttpClient, so injecting it directly would create a
     * circular dependency on HTTP_INTERCEPTORS and break the lang-file request.
     */
    private translate(key: string, params?: object): string {
        return this.injector.get(TranslateService).instant(key, params);
    }

    showSuccess(message, timer = 2000) {
      this.showToast('success', message, timer);
    }

    showDataSaved() {
      this.showSuccess(this.translate('COMMON.DATA_SAVED'));
    }

    /** Upload rejected because the picked file exceeds the allowed size. */
    showFileTooLarge(maxSizeMb: number) {
      this.showError(this.translate('COMMON.FILE_TOO_LARGE', {size: maxSizeMb}));
    }

    /**
     * Defaults are resolved from the translation files, so callers that only pass
     * a title (or nothing at all) still get a localised dialog.
     */
    confirm(title: string = null, text: string = null, confirm: string = null): Promise<SweetAlertResult> {
      return Swal.fire({
        title: title || this.translate('COMMON.CONFIRM_DELETE_TITLE'),
        icon: 'warning',
        text: text,
        showCancelButton: true,
        confirmButtonColor: '#0154c8',
        cancelButtonColor: '#d33',
        confirmButtonText: confirm || this.translate('COMMON.YES'),
        cancelButtonText: this.translate('COMMON.NO')
      })
    }

    // Three-way dialog: confirm / deny / cancel. Result carries isConfirmed / isDenied / isDismissed.
    confirmWithDeny(title: string, text: string, confirm: string, deny: string): Promise<SweetAlertResult> {
      return Swal.fire({
        title: title,
        icon: 'warning',
        text: text,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#0154c8',
        denyButtonColor: '#691969',
        cancelButtonColor: '#d33',
        confirmButtonText: confirm,
        denyButtonText: deny,
        cancelButtonText: this.translate('COMMON.CANCEL'),
      })
    }

    confirmDestructive(options: {title: string, text: string, confirm: string, expected?: string, inputLabel?: string, mismatch?: string}): Promise<SweetAlertResult> {
      const config: SweetAlertOptions = {
        title: options.title,
        icon: 'warning',
        text: options.text,
        showCancelButton: true,
        focusCancel: true,
        reverseButtons: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        customClass: {confirmButton: 'swal2-confirm--danger'},
        confirmButtonText: options.confirm,
        cancelButtonText: this.translate('COMMON.CANCEL'),
      };

      if (options.expected) {
        const expected = options.expected.trim().toLowerCase();
        config.input = 'text';
        config.inputLabel = options.inputLabel;
        config.inputPlaceholder = options.expected;
        config.inputAttributes = {autocapitalize: 'off', autocomplete: 'off', spellcheck: 'false'};
        const mismatch = options.mismatch || this.translate('COMMON.GENERAL_ERROR');
        config.inputValidator = (value: string) => (value || '').trim().toLowerCase() === expected ? null : mismatch;
      }

      return Swal.fire(config);
    }

    showInfoDialog(title: string, text: string, icon: 'warning' | 'info' | 'success' | 'error' = 'warning'): Promise<SweetAlertResult> {
      return Swal.fire({
        title: title,
        text: text,
        icon: icon,
        confirmButtonColor: '#691969',
        confirmButtonText: this.translate('COMMON.CLOSE')
      });
    }

    showError(message) {
      this.showToast('error', message, 4000);
    }

    showGeneralError() {
      this.showToast('error', this.translate('COMMON.GENERAL_ERROR'));
    }

    showToast(type, title, timer = 2000) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: timer,
        title: title,
        icon: type,
        backdrop: false,
      })
    }
}
